import { trace, getRandomInt } from 'rytm-helpers';
import * as THREE from 'three';
import gsap from 'gsap';

import Shape from './shape';
import { getAccentSoundAnalyserData } from './mixer'
import {
  FPS,
  LINE_COLOR,
  LINE_WIDTH_MIN,
  LINE_WIDTH_MAX,
  LINE_WIDTH_POINTS_MIN,
  LINE_WIDTH_POINTS_MAX,
  MIN_SHAPES_PER_ACCENT,
  MAX_SHAPES_PER_ACCENT,
  SHAPES_POINTS_CHANCE,
  SHAPE_POS_RAND_RADIUS_MIN,
  SHAPE_POS_RAND_RADIUS_MAX,
  SHAPE_POS_WANDER_MIN,
  SHAPE_POS_WANDER_MAX,
  SHAPE_ANGLE_RANDOM_F_MIN,
  SHAPE_ANGLE_RANDOM_F_MAX,
  SHAPE_ANGLE_WANDER_MIN,
  SHAPE_ANGLE_WANDER_MAX,
  ANIM_TIME_SHOW_MIN,
  ANIM_TIME_SHOW_MAX,
  ANIM_TIME_HIDE_MIN,
  ANIM_TIME_HIDE_MAX,
} from './config';

const COMP_MODEL_LINES = 'lines';
const COMP_MODEL_POINTS = 'points';

let viewporRect = {width: 0, height: 0};
let viewportDiag = 0;
let compositionParams = {
  compModel: false,
  shapesPerAccent: 0,
  lineLengthMin: 0,
  lineLengthMax: 0,
  lineLengthRandF: 1,
  lineWidth: 3,
  angleWnaderF: .2, // angle change in time, eg .2
  angleRandomF: .1, // angle randomization for each shape, eg .1
  animTimeShow: 10,
  animTimeHide: 10,
  startPosRandF: .5, // how far from center can start
  startPosWanderF: 1, // movement of start pos, eg .5
  shapePosRandRadius: .1, // randomize shape position radius, eg .1
};
// ### Global props (viewport, composition) ###
export const setCompositionParams = ( rect ) => {
  // update rectangle and viewport diagonal
  setViewportRect(rect);
  // randomize comp model (lines vs points)
  setCompModel();  
  const lineWidth = getCompLineWidth();
  const lineLengthMax = getCompLineLengthMax(lineWidth);
  const lineLengthMin = getCompLineLengthMin(lineWidth, lineLengthMax);
  compositionParams = {
    ...compositionParams,
    shapesPerAccent: getRandomInt(MIN_SHAPES_PER_ACCENT, MAX_SHAPES_PER_ACCENT),
    lineLengthMin,
    lineLengthMax,
    lineWidth,
    angleWnaderF: getRandomInt(SHAPE_ANGLE_WANDER_MIN * 100, SHAPE_ANGLE_WANDER_MAX * 100) * .01,
    angleRandomF: getRandomInt(SHAPE_ANGLE_RANDOM_F_MIN * 100, SHAPE_ANGLE_RANDOM_F_MAX * 100) * .01,
    animTimeShow: getRandomInt(ANIM_TIME_SHOW_MIN, ANIM_TIME_SHOW_MAX),
    animTimeHide: getRandomInt(ANIM_TIME_HIDE_MIN, ANIM_TIME_HIDE_MAX),
    shapePosRandRadius: getRandomInt(SHAPE_POS_RAND_RADIUS_MIN * 100, SHAPE_POS_RAND_RADIUS_MAX * 100) * .01,
    startPosWanderF: getRandomInt(SHAPE_POS_WANDER_MIN * 100, SHAPE_POS_WANDER_MAX * 100) * .01,
  }
  trace("comp params:", compositionParams);
}
const setCompModel = () => {
  compositionParams.compModel = Math.random() > (1 - SHAPES_POINTS_CHANCE * .01) ? COMP_MODEL_POINTS : COMP_MODEL_LINES;
}
const getCompLineWidth = () => {
  switch (compositionParams.compModel) {
    case COMP_MODEL_POINTS:
      return getRandomInt(LINE_WIDTH_POINTS_MIN, LINE_WIDTH_POINTS_MAX);
    default:
      return getRandomInt(LINE_WIDTH_MIN, LINE_WIDTH_MAX);
  }
}
const getCompLineLengthMax = (lineWidth) => {
  switch (compositionParams.compModel) {
    case COMP_MODEL_POINTS:
      return lineWidth * .1;
    default:
      return viewportDiag * getRandomInt(20, 150) * .01;
  }
}
const getCompLineLengthMin = (lineWidth, lineLengthMax) => {
  switch (compositionParams.compModel) {
    case COMP_MODEL_POINTS:
      return lineWidth * .07;
    default:
      return lineLengthMax * getRandomInt(1, 70) * .01;
  }
}


// create list of shapes for accent sound
export const getAccentShapes = (accentParams, rect) => {
  let shapes = [];
  // update rectangle and viewport diagonal
  setViewportRect(rect);
  // current audio properties
  const audioParams = getAccentSoundAnalyserData(accentParams.soundId);
  
  const startPos = getStartPoint(accentParams, audioParams);
  const angle = getAngle(startPos);
  const angleChangeF = Math.random()*Math.PI*2 * compositionParams.angleWnaderF * audioParams.duration * .002 * (Math.random() > .5 ? 1 : -1);
  
  // shortcuts
  const length = compositionParams.lineLengthMax;
  const lengthRandF = compositionParams.lineLengthRandF;
  // pattern for sound ids
  const idPattern = getSoundIdPattern(accentParams.soundId);
  // total shapes
  const shapesTotal = getShapesTotal(audioParams.duration);
  for (let i = 0; i < shapesTotal; i++) {
    const perc = Math.round((i / shapesTotal) * 100) / 100;
    const id = idPattern.replace('%%i%%', i);
    const lifetime = audioParams.duration * FPS;
    const myAngle = angle + angleChangeF * i + Math.random()*Math.PI*2 * compositionParams.angleRandomF;
    const startP = getShapeStartPoint(startPos, myAngle, perc);
    const shapeParams = {
      id,
      ...accentParams,
      x: startP.x,
      y: startP.y,
      angle: myAngle,
      length: length + getRandomInt(-length * lengthRandF, length * lengthRandF),
      lengthMax: length + length * lengthRandF,
      lengthMin: compositionParams.lineLengthMin,
      lengthThreshold: compositionParams.lengthThreshold,
      lifetime: lifetime - getRandomInt(0, lifetime * .3), 
      animTimeShow: compositionParams.animTimeShow,
      animTimeHide: compositionParams.animTimeHide,
      // delay - when is the shape visible
      delay: lifetime * perc * getRandomInt(60, 100) * .01,
      materialProps: {
        color: LINE_COLOR,
        linewidth: compositionParams.lineWidth,
      },
    }
    shapes.push(new Shape(shapeParams));
  }

  return shapes;
}
// get angle (base) position dependend (p = startingPoint)
const getAngle = (p) => {
  // test rectangle
  const tr = {
    x0: -viewporRect.width / 7,
    y0: viewporRect.height / 7,
    x1: viewporRect.width / 7,
    y1: -viewporRect.height / 7,
  }
  if (p.x < tr.x0 && p.y > tr.y0) {
    return getRandomInt(76, 99) * 0.01 *Math.PI*2;
  } else if (p.x > tr.x1 && p.y > tr.y0) {
    return getRandomInt(52, 72) * 0.01 *Math.PI*2;
  } else if (p.x > tr.x1 && p.y < tr.y1) {
    return getRandomInt(27, 48) * 0.01 *Math.PI*2;
  } else if (p.x < tr.x0 && p.y < tr.y1) {
    return getRandomInt(2, 22) * 0.01 *Math.PI*2;
  } else if (p.x < tr.x0) {
    return getRandomInt(80, 120) * 0.01 *Math.PI*2;
  } else if (p.x > tr.x1) {
    return getRandomInt(30, 70) * 0.01 *Math.PI*2;
  } else if (p.y > tr.y0) {
    return getRandomInt(55, 95) * 0.01 *Math.PI*2;
  } else if (p.y < tr.y1) {
    return getRandomInt(5, 45) * 0.01 *Math.PI*2;
  }
  return Math.random()*Math.PI*2;
}
// start point for whole accent
const getStartPoint = (accentParams, audioParams) => {
  return {
    x: accentParams.stereo * (viewporRect.width / 2) * compositionParams.startPosRandF,
    y: getRandomInt(-viewporRect.height / 2, viewporRect.height / 2) * compositionParams.startPosRandF,
  };
}
// start point for single shape
const getShapeStartPoint = (p, angle, perc) => {
  // calculate start point 0
  const dist = viewportDiag * .5 * compositionParams.startPosWanderF;
  const p0 = {
    x: p.x + Math.cos(angle) * dist * perc,
    y: p.y + Math.sin(angle) * dist * perc,
  }
  // randomize position
  const randomAngle = Math.random()*Math.PI*2;
  const randomRadius = Math.random();
  return {
    x: p0.x + Math.cos(randomAngle) * viewportDiag * compositionParams.shapePosRandRadius * randomRadius,
    y: p0.y + Math.sin(randomAngle) * viewportDiag * compositionParams.shapePosRandRadius * randomRadius,
  }
}


const setViewportRect = (rect) => {
  viewporRect = rect;
  viewportDiag = Math.sqrt(viewporRect.width * viewporRect.width + viewporRect.height * viewporRect.height);
}
const getSoundIdPattern = (soundId) => {
  return soundId + '_%%i%%_' + getRandomInt(9999, 9999999).toString();
}
const getShapesTotal = (duration) => {
  const shapesTotal = Math.round(compositionParams.shapesPerAccent * duration);
  const max = MAX_SHAPES_PER_ACCENT * 5;
  return shapesTotal > max ? max : shapesTotal;
}


export const getLineData = () => {
  const maxX = viewporRect.width / 2;
  const maxY = viewporRect.height / 2;
  const diagonal = Math.sqrt(maxX * maxX + maxY * maxY);
  const x = getRandomInt(-maxX, maxX);
  const y = getRandomInt(-maxY, maxY);
  const angle = getRandomInt(0, 360);
  const length = getRandomInt(2, diagonal * getAudioVolume());
  return {
    x,
    y,
    angle,
    length,
  }
}
export const getShapePoints = () => {
  const points = [];
  const maxX = viewporRect.width / 2;
  const maxY = viewporRect.height / 2;

  points.push({
    x: getRandomInt(-maxX, maxX), 
    y: getRandomInt(-maxY, maxY),
  });
  points.push({
    x: getRandomInt(-maxX, maxX), 
    y: getRandomInt(-maxY, maxY),
  });
  return points;
}


// let audioData = null;
// // ### audio data helpers ###
// const getAudioPan = () => {
//   let pan = 0;
//   if (audioData?.channels) {
//     audioData.channels.forEach((channel, index) => {
//       pan += (channel.vol * (index === 0 ? -1 : 1));
//     });
//   }
//   return pan;
// }
// // get audio average freq volume
// const getAudioVolume = () => {
//   let vol = 0;
//   if (audioData?.channels) {
//     audioData.channels.forEach((channel) => {
//       vol += channel.vol;
//     });
//     vol = vol / audioData.channels.length;
//   }
//   return vol;
// }
